import { useState, useRef, useEffect } from "react";

interface Message {
  id: number;
  from: "bot" | "user";
  text: string;
  time: string;
}

const BOT_NAME = "Ultimate Support";
const BOT_AVATAR = "https://readdy.ai/api/search-image?query=friendly%20HVAC%20technician%20avatar%20icon%20professional%20smiling%20face%20minimal%20flat%20design%20on%20white%20background%20simple%20illustration&width=80&height=80&seq=bot-avatar-001&orientation=squarish";

const INITIAL_MESSAGES: Message[] = [
  {
    id: 1,
    from: "bot",
    text: "Hey there! Welcome to Ultimate Heating & Cooling. How can we help you today?",
    time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
  },
  {
    id: 2,
    from: "bot",
    text: "You can ask about scheduling service, pricing, or leave your info and we'll get back to you ASAP!",
    time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
  },
];

const QUICK_REPLIES = [
  "Schedule a service",
  "Get a free quote",
  "Emergency service",
];

const AUTO_REPLIES: Record<string, string> = {
  schedule: "Great! You can book a visit by filling out a quick form. Or call us at (959)241-8524 and we'll get you set up right away!",
  quote: "We offer free estimates! Just leave your name, number and a brief description of what you need — we'll call you within 1 business hour.",
  emergency: "We're available for emergencies! Call us NOW at (959)241-8524 for immediate dispatch.",
  default: "Thanks for reaching out! One of our team members will follow up shortly. For urgent needs, call us at (959)241-8524.",
};

function getAutoReply(msg: string): string {
  const lower = msg.toLowerCase();
  if (lower.includes("schedul") || lower.includes("appoint") || lower.includes("book")) return AUTO_REPLIES.schedule;
  if (lower.includes("quote") || lower.includes("price") || lower.includes("cost") || lower.includes("free")) return AUTO_REPLIES.quote;
  if (lower.includes("emergency") || lower.includes("urgent") || lower.includes("asap")) return AUTO_REPLIES.emergency;
  return AUTO_REPLIES.default;
}

type ChatView = "chat" | "form" | "done";

export default function LiveChat() {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<ChatView>("chat");
  const [messages, setMessages] = useState<Message[]>(INITIAL_MESSAGES);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [unread, setUnread] = useState(1);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setUnread(0);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    }
  }, [open, messages]);

  const sendMessage = (text: string) => {
    if (!text.trim()) return;
    const now = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    const userMsg: Message = { id: Date.now(), from: "user", text, time: now };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setTyping(true);
    setTimeout(() => {
      const reply = getAutoReply(text);
      setTyping(false);
      setMessages((prev) => [
        ...prev,
        { id: Date.now() + 1, from: "bot", text: reply, time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) },
      ]);
    }, 1200);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) return;
    setSubmitting(true);
    try {
      const body = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
      });
      await fetch("https://readdy.ai/api/form/d6uvqgvb245v7h34ohqg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setView("done");
    } catch {
      // still show success
      setView("done");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 flex items-center justify-center bg-[#C8102E] rounded-full cursor-pointer transition-transform hover:scale-110 active:scale-95"
        style={{ boxShadow: "0 4px 24px rgba(200,16,46,0.4)" }}
        aria-label="Open live chat"
      >
        {open ? (
          <i className="ri-close-line text-white text-2xl"></i>
        ) : (
          <>
            <i className="ri-chat-3-line text-white text-2xl"></i>
            {unread > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center bg-[#0B2447] text-white text-[10px] font-bold rounded-full">
                {unread}
              </span>
            )}
          </>
        )}
      </button>

      {/* Chat window */}
      {open && (
        <div
          className="fixed bottom-24 right-6 z-50 w-[360px] rounded-2xl overflow-hidden flex flex-col"
          style={{
            boxShadow: "0 8px 40px rgba(11,36,71,0.18)",
            maxHeight: "520px",
          }}
        >
          {/* Header */}
          <div className="bg-[#0B2447] px-4 py-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0">
              <img src={BOT_AVATAR} alt="Support" className="w-full h-full object-cover" />
            </div>
            <div className="flex-1">
              <div className="text-white font-bold text-sm" style={{ fontFamily: "'Montserrat', sans-serif" }}>{BOT_NAME}</div>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-green-400 inline-block"></span>
                <span className="text-green-400 text-[11px]">Online — replies in minutes</span>
              </div>
            </div>
            <button
              onClick={() => setView(view === "form" ? "chat" : "form")}
              className="text-white/60 hover:text-white text-xs cursor-pointer whitespace-nowrap transition-colors"
            >
              {view === "form" ? "← Chat" : "Leave info"}
            </button>
          </div>

          {/* Body */}
          <div className="bg-white flex-1 overflow-y-auto flex flex-col" style={{ minHeight: 0, maxHeight: "360px" }}>
            {view === "chat" && (
              <div className="flex-1 p-4 flex flex-col gap-3 overflow-y-auto">
                {messages.map((msg) => (
                  <div key={msg.id} className={`flex gap-2 ${msg.from === "user" ? "flex-row-reverse" : "flex-row"}`}>
                    {msg.from === "bot" && (
                      <div className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0 mt-1">
                        <img src={BOT_AVATAR} alt="Bot" className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className={`max-w-[78%] ${msg.from === "user" ? "items-end" : "items-start"} flex flex-col gap-0.5`}>
                      <div
                        className={`px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                          msg.from === "user"
                            ? "bg-[#C8102E] text-white rounded-tr-sm"
                            : "bg-gray-100 text-[#0B2447] rounded-tl-sm"
                        }`}
                      >
                        {msg.text}
                      </div>
                      <span className="text-[10px] text-gray-400 px-1">{msg.time}</span>
                    </div>
                  </div>
                ))}
                {typing && (
                  <div className="flex gap-2">
                    <div className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0 mt-1">
                      <img src={BOT_AVATAR} alt="Bot" className="w-full h-full object-cover" />
                    </div>
                    <div className="bg-gray-100 rounded-2xl rounded-tl-sm px-4 py-3 flex items-center gap-1">
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></span>
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></span>
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></span>
                    </div>
                  </div>
                )}
                <div ref={bottomRef} />
              </div>
            )}

            {view === "form" && (
              <form
                data-readdy-form
                onSubmit={handleFormSubmit}
                className="flex-1 p-4 flex flex-col gap-3 overflow-y-auto"
              >
                <p className="text-[#0B2447] text-sm font-semibold">Leave your info and we&apos;ll reach out shortly!</p>
                <input
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  placeholder="Your name *"
                  required
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C8102E] transition-colors"
                />
                <input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleFormChange}
                  placeholder="Email address *"
                  required
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C8102E] transition-colors"
                />
                <input
                  name="phone"
                  value={formData.phone}
                  onChange={handleFormChange}
                  placeholder="Phone number"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C8102E] transition-colors"
                />
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleFormChange}
                  placeholder="How can we help? *"
                  required
                  maxLength={500}
                  rows={3}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C8102E] transition-colors resize-none"
                />
                <div className="text-right text-[10px] text-gray-400">{formData.message.length}/500</div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#C8102E] text-white text-sm font-bold py-2.5 rounded-full hover:bg-[#A8091D] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {submitting ? "Sending..." : "Send Message"}
                </button>
              </form>
            )}

            {view === "done" && (
              <div className="flex-1 flex flex-col items-center justify-center gap-4 p-8 text-center">
                <div className="w-14 h-14 flex items-center justify-center bg-green-100 rounded-full">
                  <i className="ri-check-line text-green-500 text-2xl"></i>
                </div>
                <div>
                  <div className="text-[#0B2447] font-bold text-base mb-1">Got it, thanks!</div>
                  <div className="text-gray-500 text-sm">We&apos;ll be in touch within 1 business hour. For urgent needs, call <a href="tel:9592418524" className="text-[#C8102E] font-semibold">(959)241-8524</a>.</div>
                </div>
                <button
                  onClick={() => { setView("chat"); setFormData({ name: "", email: "", phone: "", message: "" }); }}
                  className="text-[#C8102E] text-sm font-semibold cursor-pointer hover:underline"
                >
                  Back to chat
                </button>
              </div>
            )}
          </div>

          {/* Quick replies (chat view only) */}
          {view === "chat" && (
            <div className="bg-gray-50 border-t border-gray-100 px-3 py-2 flex gap-1.5 overflow-x-auto">
              {QUICK_REPLIES.map((qr) => (
                <button
                  key={qr}
                  onClick={() => sendMessage(qr)}
                  className="flex-shrink-0 text-[11px] font-semibold text-[#0B2447] bg-white border border-gray-200 rounded-full px-3 py-1 hover:border-[#C8102E] hover:text-[#C8102E] transition-colors cursor-pointer whitespace-nowrap"
                >
                  {qr}
                </button>
              ))}
            </div>
          )}

          {/* Input (chat view only) */}
          {view === "chat" && (
            <div className="bg-white border-t border-gray-100 flex items-center gap-2 px-3 py-2.5">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendMessage(input)}
                placeholder="Type a message..."
                className="flex-1 text-sm outline-none text-[#0B2447] placeholder-gray-400"
              />
              <button
                onClick={() => sendMessage(input)}
                disabled={!input.trim()}
                className="w-8 h-8 flex items-center justify-center bg-[#C8102E] rounded-full cursor-pointer disabled:opacity-40 transition-opacity"
              >
                <i className="ri-send-plane-fill text-white text-sm"></i>
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
